import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getStaticVideoDetails, mockExtractFrame, mockExtractFramesBatch } from '../services/staticData';

const VideoDetails = ({ vehicle, minuteData, date, onBack }) => {
  const navigate = useNavigate();
  const params = useParams();
  const [videoDetails, setVideoDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentTrajectoryIndex, setCurrentTrajectoryIndex] = useState(0);
  const [videoPlaybackTime, setVideoPlaybackTime] = useState(0);
  const [videoLoadingStates, setVideoLoadingStates] = useState({
    left: false,
    center: false,
    right: false
  });
  const [captureStates, setCaptureStates] = useState({
    left: false,
    center: false,
    right: false
  });
  const [selectedFrames, setSelectedFrames] = useState({
    left: [],
    center: [],
    right: []
  });
  const leftVideoRef = useRef(null);
  const centerVideoRef = useRef(null);
  const rightVideoRef = useRef(null);

  useEffect(() => {
    if (vehicle && minuteData && date && minuteData.minute !== undefined && minuteData.minute !== null) {
      fetchVideoDetails();
    } else {
      console.log('VideoDetails - useEffect: Missing required data:', {
        vehicle: !!vehicle,
        minuteData: !!minuteData,
        date: !!date,
        minute: minuteData?.minute
      });
    }
  }, [vehicle, minuteData, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVideoDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Debug logging
      console.log('VideoDetails - fetchVideoDetails called with:', {
        vehicleId: vehicle?.id,
        date,
        minute: minuteData?.minute,
        minuteData
      });
      
      // Validate required data
      if (!vehicle?.id) {
        throw new Error('Vehicle ID is missing');
      }
      if (!date) {
        throw new Error('Date is missing');
      }
      if (minuteData?.minute === undefined || minuteData?.minute === null) {
        throw new Error(`Minute is missing or invalid: ${minuteData?.minute}`);
      }
      
      const data = await getStaticVideoDetails(vehicle.id, date, minuteData.minute);
      setVideoDetails(data);
    } catch (err) {
      console.error('VideoDetails - Error fetching video details:', err);
      const errorMessage = err.message || 'Failed to load video details';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to load video details');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minute) => {
    const hours = Math.floor(minute / 60);
    const mins = minute % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Helper function to check if URL is a valid video URL
  const isValidVideoUrl = (url) => {
    if (!url) return false;
    // Check for common video file extensions or Azure Blob URLs
    const videoExtensions = /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?.*)?$/i;
    const isAzureBlob = url.includes('blob.core.windows.net') || url.includes('azure');
    return videoExtensions.test(url) || isAzureBlob;
  };

  // Helper functions for video loading states
  const setVideoLoading = (camera, loading) => {
    setVideoLoadingStates(prev => ({
      ...prev,
      [camera]: loading
    }));
  };

  const handleVideoLoadStart = (camera) => {
    setVideoLoading(camera, true);
  };

  const handleVideoLoaded = (camera) => {
    setVideoLoading(camera, false);
    console.log(`${camera} video loaded successfully`);
  };

  const handleVideoError = (camera, error) => {
    setVideoLoading(camera, false);
    console.error(`${camera} camera video error:`, error);
  };

  // Add frame timestamp to selection
  const addFrameTimestamp = (camera) => {
    const videoRef = camera === 'left' ? leftVideoRef : 
                    camera === 'center' ? centerVideoRef : rightVideoRef;
    
    if (videoRef && videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      setSelectedFrames(prev => ({
        ...prev,
        [camera]: [...prev[camera], currentTime]
      }));
    }
  };

  // Remove frame timestamp from selection
  const removeFrameTimestamp = (camera, index) => {
    setSelectedFrames(prev => ({
      ...prev,
      [camera]: prev[camera].filter((_, i) => i !== index)
    }));
  };

  // Clear all selected frames for a camera
  const clearSelectedFrames = (camera) => {
    setSelectedFrames(prev => ({
      ...prev,
      [camera]: []
    }));
  };

  // Save all selected frames for a camera
  const saveSelectedFrames = async (camera) => {
    if (selectedFrames[camera].length === 0) {
      alert(`No frames selected for ${camera} camera`);
      return;
    }

    try {
      setCaptureStates(prev => ({ ...prev, [camera]: true }));
      
      const videoRef = camera === 'left' ? leftVideoRef : 
                      camera === 'center' ? centerVideoRef : rightVideoRef;
      
      if (!videoRef || !videoRef.current) {
        throw new Error(`${camera} video not available`);
      }

      const video = videoRef.current;
      
      // Prepare frame extraction data with multiple timestamps
      const frameData = {
        camera: camera,
        vehicle_id: vehicle.id,
        date: date,
        minute: minuteData.minute,
        timestamp: new Date().toISOString(),
        video_url: video.src,
        video_width: video.videoWidth || 640,
        video_height: video.videoHeight || 480,
        video_times: selectedFrames[camera],
        capture_method: 'backend_extraction'
      };
      
      // Choose the appropriate API endpoint based on number of frames
      const isMultipleFrames = selectedFrames[camera].length > 1;
      const apiFunction = isMultipleFrames ? mockExtractFramesBatch : mockExtractFrame;
      
      // Use the appropriate mock API service
      const result = await apiFunction(frameData);
      console.log(`${camera} ${isMultipleFrames ? 'multiple' : 'single'} frame(s) extraction requested successfully:`, result);
      alert(`${camera} camera: ${selectedFrames[camera].length} frame(s) extraction requested! Backend will extract image(s) at the selected timestamp(s).`);
      
      // Clear selected frames after successful save
      clearSelectedFrames(camera);
      
    } catch (error) {
      console.error(`Error saving selected frames for ${camera} camera:`, error);
      alert(`Failed to save selected frames for ${camera} camera: ${error.message}. Please try again.`);
    } finally {
      setCaptureStates(prev => ({ ...prev, [camera]: false }));
    }
  };

  // Capture function to capture video frame and send via API
  /*
  const captureVideoFrame = async (camera) => {
    // // Get the appropriate video reference at the start
    // const videoRef = camera === 'front' ? frontVideoRef : 
    //                 camera === 'rear' ? rearVideoRef : sideVideoRef;
    
    try {
      setCaptureStates(prev => ({ ...prev, [camera]: true }));
      
      if (!videoRef || !videoRef.current) {
        throw new Error(`${camera} video not available`);
      }

      const video = videoRef.current;
      
      // Check if video is ready
      if (video.readyState < 2) {
        throw new Error(`${camera} video not ready for capture`);
      }
      
      console.log(`${camera} video info:`, {
        currentTime: video.currentTime,
        duration: video.duration,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        readyState: video.readyState,
        src: video.src
      });
      
      // Method 1: Try direct canvas capture with CORS handling
      let blob = null;
      let captureSuccess = false;
      
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions
        const width = video.videoWidth || 640;
        const height = video.videoHeight || 480;
        canvas.width = width;
        canvas.height = height;
        
        console.log(`Attempting canvas capture for ${camera} with dimensions: ${width}x${height}`);
        
        // Draw the current video frame to canvas
        ctx.drawImage(video, 0, 0, width, height);
        
        // Try to convert to blob
        blob = await new Promise((resolve, reject) => {
          canvas.toBlob((result) => {
            if (result && result.size > 0) {
              console.log(`${camera} canvas capture successful, blob size:`, result.size);
              resolve(result);
            } else {
              reject(new Error('Canvas to blob conversion failed or empty blob'));
            }
          }, 'image/jpeg', 0.9);
        });
        
        captureSuccess = true;
        
      } catch (canvasError) {
        console.log(`Canvas method failed for ${camera}:`, canvasError.message);
        
        // Method 2: Try with a different approach - create image from canvas data
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          canvas.width = 640;
          canvas.height = 480;
          
          // Try to draw image
          ctx.drawImage(video, 0, 0, 640, 480);
          
          // Convert canvas to data URL first, then to blob
          const dataURL = canvas.toDataURL('image/jpeg', 0.8);
          
          // Convert data URL to blob
          const response = await fetch(dataURL);
          blob = await response.blob();
          
          console.log(`${camera} dataURL method successful, blob size:`, blob.size);
          captureSuccess = true;
          
        } catch (dataURLError) {
          console.log(`DataURL method also failed for ${camera}:`, dataURLError.message);
        }
      }
      
      // If we successfully captured an image, send it
      if (captureSuccess && blob && blob.size > 0) {
        console.log(`Sending ${camera} capture to API...`);
        
        // Create FormData for API call
        const formData = new FormData();
        formData.append('image', blob, `${camera}_capture_${Date.now()}.jpg`);
        formData.append('camera', camera);
        formData.append('vehicle_id', vehicle.id);
        formData.append('date', date);
        formData.append('minute', minuteData.minute);
        formData.append('timestamp', new Date().toISOString());
        formData.append('video_time', video.currentTime);
        formData.append('video_url', video.src);
        
        // Send to API
        const response = await fetch('/api/capture-image', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Failed to send capture: ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log(`${camera} capture sent successfully:`, result);
        
        // Show success message
        alert(`${camera} camera frame captured and sent successfully!`);
        return;
      }
      
      // If frontend capture failed, use backend method
      console.log(`Frontend capture failed for ${camera}, using backend method...`);
      
      // Prepare frame extraction data
      const frameData = {
        camera: camera,
        vehicle_id: vehicle.id,
        date: date,
        minute: minuteData.minute,
        timestamp: new Date().toISOString(),
        video_time: video.currentTime,
        video_url: video.src,
        video_width: video.videoWidth || 640,
        video_height: video.videoHeight || 480,
        capture_method: 'backend_extraction'
      };
      
      // Use the monitoringAPI service for single frame extraction
      const result = await monitoringAPI.extractFrame(frameData);
      console.log(`${camera} backend capture requested successfully:`, result);
      alert(`${camera} camera frame capture requested! Backend will extract the image at the exact moment.`);
      
    } catch (error) {
      console.error(`Error capturing ${camera} video:`, error);
      alert(`Failed to capture ${camera} video: ${error.message}. Please try again.`);
    } finally {
      setCaptureStates(prev => ({ ...prev, [camera]: false }));
    }
  };
  */

  // Synchronized play/pause all videos
  const togglePlayPause = () => {
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);
    
    console.log('Toggle play/pause:', newPlayingState);
    
    // Control all video elements simultaneously
    const videoRefs = [leftVideoRef, centerVideoRef, rightVideoRef];
    const videoNames = ['left', 'center', 'right'];
    
    videoRefs.forEach((videoRef, index) => {
      if (videoRef && videoRef.current) {
        console.log(`Controlling ${videoNames[index]} video:`, {
          currentTime: videoRef.current.currentTime,
          duration: videoRef.current.duration,
          readyState: videoRef.current.readyState
        });
        
        if (newPlayingState) {
          videoRef.current.play().catch(error => {
            console.error(`Error playing ${videoNames[index]} video:`, error);
          });
        } else {
          videoRef.current.pause();
        }
      } else {
        console.warn(`${videoNames[index]} video ref not available`);
      }
    });
  };

  // Handle individual video play/pause events to sync state
  const handleVideoPlay = () => {
    if (!isPlaying) {
      setIsPlaying(true);
    }
  };

  const handleVideoPause = () => {
    if (isPlaying) {
      setIsPlaying(false);
    }
  };

  // Update GPS position based on video playback time
  const updateGpsPositionFromVideoTime = (videoTime) => {
    if (!trajectory || !trajectory.length || !videoDuration) return;
    
    const startTime = new Date(videoDuration.start_time).getTime();
    const currentTime = startTime + (videoTime * 1000);
    
    let closestIndex = 0;
    let minDiff = Infinity;
    
    trajectory.forEach((point, index) => {
      const pointTime = new Date(point.timestamp).getTime();
      const diff = Math.abs(currentTime - pointTime);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = index;
      }
    });
    
    setCurrentTrajectoryIndex(closestIndex);
    setCurrentTime(videoTime);
  };

  // Handle time navigation based on trajectory
  const handleTimeChange = (newTime) => {
    setCurrentTime(newTime);
    setVideoPlaybackTime(newTime);
    
    // Find the closest trajectory point based on time
    const trajectoryIndex = findTrajectoryIndexByTime(newTime);
    setCurrentTrajectoryIndex(trajectoryIndex);
    
    // Sync all videos to the new time
    const videoRefs = [leftVideoRef, centerVideoRef, rightVideoRef];
    const videoNames = ['left', 'center', 'right'];
    
    videoRefs.forEach((videoRef, index) => {
      if (videoRef && videoRef.current) {
        console.log(`Syncing ${videoNames[index]} video to time:`, newTime);
        videoRef.current.currentTime = newTime;
      }
    });
  };

  // Handle video time updates
  const handleVideoTimeUpdate = (camera) => {
    const videoRef = camera === 'left' ? leftVideoRef : 
                    camera === 'center' ? centerVideoRef : rightVideoRef;
    
    if (videoRef && videoRef.current) {
      const currentVideoTime = videoRef.current.currentTime;
      setVideoPlaybackTime(currentVideoTime);
      updateGpsPositionFromVideoTime(currentVideoTime);
      
      // Debug logging for GPS sync
      if (Math.floor(currentVideoTime) % 5 === 0) { // Log every 5 seconds
        console.log(`GPS Sync - ${camera} camera:`, {
          videoTime: currentVideoTime,
          trajectoryLength: trajectory ? trajectory.length : 0,
          currentTrajectoryIndex: currentTrajectoryIndex
        });
      }
    }
  };


  // Find trajectory index based on time
  const findTrajectoryIndexByTime = (timeInSeconds) => {
    if (!trajectory || !trajectory.length || !videoDuration) return 0;
    
    const startTime = new Date(videoDuration.start_time).getTime();
    const currentTime = startTime + (timeInSeconds * 1000);
    
    let closestIndex = 0;
    let minDiff = Infinity;
    
    trajectory.forEach((point, index) => {
      const pointTime = new Date(point.timestamp).getTime();
      const diff = Math.abs(currentTime - pointTime);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = index;
      }
    });
    
    return closestIndex;
  };

  // Get current GPS point
  const getCurrentGpsPoint = () => {
    if (!trajectory || !trajectory.length) return null;
    return trajectory[currentTrajectoryIndex] || trajectory[0];
  };


  // Check if we have valid data to proceed
  if (!vehicle || !minuteData || !date || minuteData.minute === undefined || minuteData.minute === null) {
    return (
      <div className="container">
        <div className="error">
          Invalid data provided. Missing vehicle, date, or minute information.
          <br />
          <small>Debug info: vehicle={!!vehicle}, minuteData={!!minuteData}, date={!!date}, minute={minuteData?.minute}</small>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(`/timeline/${params.vehicleId}`)}>
          ← Back to Timeline
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading video details...</div>;
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate(`/timeline/${params.vehicleId}`)}>
          ← Back to Timeline
        </button>
      </div>
    );
  }

  // Extract video URLs from the new API response format - using signed URLs
  const videos = [
    { 
      name: 'Left Camera', 
      url: videoDetails?.video_urls?.left_video_signed, 
      available: !!videoDetails?.video_urls?.left_video_signed 
    },
    { 
      name: 'Center Camera', 
      url: videoDetails?.video_urls?.center_video_signed, 
      available: !!videoDetails?.video_urls?.center_video_signed 
    },
    { 
      name: 'Right Camera', 
      url: videoDetails?.video_urls?.right_video_signed, 
      available: !!videoDetails?.video_urls?.right_video_signed 
    }
  ];
  
  // Debug video URLs
  console.log('Video URLs:', {
    left: videoDetails?.video_urls?.left_video_signed,
    center: videoDetails?.video_urls?.center_video_signed,
    right: videoDetails?.video_urls?.right_video_signed
  });

  // Extract trajectory data from the new API response format
  const trajectory = videoDetails?.gps_trajectory || [];
  const videoDuration = videoDetails?.video_duration;

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <button className="btn btn-secondary" onClick={() => navigate(`/timeline/${params.vehicleId}`)} style={{ marginRight: '20px' }}>
            ← Back to Timeline
          </button>
          <h1 style={{ display: 'inline' }}>Video Details</h1>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '20px' }}>
          {vehicle.vehicle_name} - {date} at {formatTime(minuteData.minute)}
        </h3>

        {/* Video Players and Map Section */}
        <div className="video-section">
          <h4 style={{ marginBottom: '20px', color: '#333' }}>Video Players & Location</h4>
          
          {/* GPS Map Section - Above Videos */}
            {trajectory && trajectory.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
                <h5 style={{ marginBottom: '10px', color: '#555' }}>GPS Trajectory Map</h5>
                <div className="map-container" style={{ 
                height: '200px',
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  border: '1px solid #ddd',
                  position: 'relative'
                }}>
                  {/* Real GPS-based Map */}
                  {(() => {
                    // Calculate map bounds from GPS data
                    const lats = trajectory.map(p => p.latitude);
                    const lngs = trajectory.map(p => p.longitude);
                    const minLat = Math.min(...lats);
                    const maxLat = Math.max(...lats);
                    const minLng = Math.min(...lngs);
                    const maxLng = Math.max(...lngs);
                    
                    // Add padding to bounds
                    const latPadding = (maxLat - minLat) * 0.1;
                    const lngPadding = (maxLng - minLng) * 0.1;
                    
                    const bounds = {
                      minLat: minLat - latPadding,
                      maxLat: maxLat + latPadding,
                      minLng: minLng - lngPadding,
                      maxLng: maxLng + lngPadding
                    };
                    
                    // Convert GPS coordinates to map position (0-100%)
                    const gpsToMapPos = (lat, lng) => {
                      const x = ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * 100;
                      const y = ((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat)) * 100;
                      return { 
                        x: Math.max(0, Math.min(100, Math.round(x * 100) / 100)), 
                        y: Math.max(0, Math.min(100, Math.round(y * 100) / 100)) 
                      };
                    };
                    
                    return (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
                      }}>
                        {/* OpenStreetMap iframe with calculated bounds and GPS marker */}
                        <iframe
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${bounds.minLng},${bounds.minLat},${bounds.maxLng},${bounds.maxLat}&layer=mapnik&marker=${getCurrentGpsPoint()?.latitude},${getCurrentGpsPoint()?.longitude}`}
                          width="100%"
                          height="100%"
                          style={{ 
                            border: 0,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 1
                          }}
                          loading="lazy"
                          title="GPS Trajectory Map"
                        />
                        
                        {/* Overlay with GPS path line */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          zIndex: 2,
                          pointerEvents: 'none'
                        }}>
                          {/* Line following GPS trajectory from start to current position */}
                          {getCurrentGpsPoint() && trajectory && trajectory.length > 0 && currentTrajectoryIndex > 0 && 
                           bounds.minLat !== bounds.maxLat && bounds.minLng !== bounds.maxLng && (
                            <svg
                              width="100%"
                              height="100%"
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0
                              }}
                            >
                              <polyline
                                points={trajectory.slice(0, currentTrajectoryIndex + 1).map((point) => {
                                  const pos = gpsToMapPos(point.latitude, point.longitude);
                                  return `${pos.x.toFixed(2)}%,${pos.y.toFixed(2)}%`;
                                }).join(' ')}
                                fill="none"
                                stroke="#ff4444"
                                strokeWidth="3"
                                opacity="0.8"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Trajectory Info Overlay */}
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    maxWidth: '200px',
                    zIndex: 5
                  }}>
                    <div><strong>Location:</strong> Bangalore, India</div>
                    <div><strong>Trajectory Points:</strong> {trajectory ? trajectory.length : 0}</div>
                    <div><strong>Current Point:</strong> {currentTrajectoryIndex + 1}</div>
                    <div><strong>Speed:</strong> {getCurrentGpsPoint()?.speed?.toFixed(1)} km/h</div>
                    <div><strong>Duration:</strong> {videoDuration?.duration_seconds}s</div>
                    <div><strong>Video Time:</strong> {videoPlaybackTime.toFixed(1)}s</div>
                  </div>

                  {/* Route Info */}
                  {trajectory && trajectory.length > 1 && (
                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '10px',
                      backgroundColor: 'rgba(0,123,255,0.9)',
                      color: 'white',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      zIndex: 5
                    }}>
                      📍 GPS Route: {trajectory ? trajectory.length : 0} points
                    </div>
                  )}

                  {/* Current Location Info */}
                  {getCurrentGpsPoint() && (
                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      backgroundColor: 'rgba(255,68,68,0.9)',
                      color: 'white',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      zIndex: 5
                    }}>
                      📍 {getCurrentGpsPoint().latitude.toFixed(6)}, {getCurrentGpsPoint().longitude.toFixed(6)}
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Three Videos in Horizontal Row */}
          <div style={{ 
            display: 'flex', 
            gap: '15px', 
            width: '100%',
            maxWidth: '100%',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            {/* Front Camera */}
            <div style={{ 
              flex: '1 1 300px', 
              minWidth: '280px',
              maxWidth: 'calc(33.333% - 10px)',
              width: '100%'
            }}>
              <h5 style={{ marginBottom: '10px', color: '#555', textAlign: 'center' }}>Front Camera</h5>
              {videos[0]?.available && videos[0]?.url && isValidVideoUrl(videos[0].url) ? (
                <div className={`video-container ${videoLoadingStates.left ? 'loading' : ''}`} style={{ 
                  position: 'relative', 
                  width: '100%',
                  height: '0',
                  paddingBottom: '56.25%', // 16:9 aspect ratio
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: 'transparent',
                  border: 'none',
                  boxShadow: 'none'
                }}>
                  <video
                    ref={leftVideoRef}
                    src={videos[0].url}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 0,
                      backgroundColor: 'transparent',
                      objectFit: 'cover',
                      filter: 'none',
                      opacity: 1,
                      brightness: 1,
                      contrast: 1,
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                    preload="metadata"
                    autoPlay={true}
                    muted={true}
                    loop={true}
                    playsInline={true}
                    title="Front Camera Video"
                    onLoadStart={() => handleVideoLoadStart('left')}
                    onLoadedData={() => handleVideoLoaded('left')}
                    onError={(e) => handleVideoError('left', e)}
                    onPlay={handleVideoPlay}
                    onPause={handleVideoPause}
                    onTimeUpdate={() => handleVideoTimeUpdate('left')}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  color: '#666',
                  border: '2px solid #e9ecef',
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {videos[0]?.available ? 'Video format not supported or loading...' : 'No video available'}
                </div>
              )}
              
              {/* Frame Selection Controls for Front Camera */}
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                <div style={{ marginBottom: '8px' }}>
                  <button
                    onClick={() => addFrameTimestamp('left')}
                    disabled={!videos[0]?.available}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: videos[0]?.available ? 'pointer' : 'not-allowed',
                      marginRight: '5px',
                      fontWeight: '500'
                    }}
                  >
                    ➕ Add Frame ({videoPlaybackTime.toFixed(1)}s)
                  </button>
                  <button
                    onClick={() => clearSelectedFrames('left')}
                    disabled={selectedFrames.left.length === 0}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedFrames.left.length > 0 ? 'pointer' : 'not-allowed',
                      fontWeight: '500'
                    }}
                  >
                    🗑️ Clear All
                  </button>
                </div>
                
                {/* Selected Frames Display */}
                {selectedFrames.left.length > 0 && (
                  <div style={{ 
                    marginBottom: '8px', 
                    padding: '6px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px',
                    fontSize: '10px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      Selected Frames ({selectedFrames.left.length}):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                      {selectedFrames.left.map((time, index) => (
                        <span
                          key={index}
                          style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '9px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px'
                          }}
                        >
                          {time.toFixed(1)}s
                          <button
                            onClick={() => removeFrameTimestamp('left', index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '8px',
                              padding: '0',
                              marginLeft: '2px'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Save Button */}
                <button
                  onClick={() => saveSelectedFrames('left')}
                  disabled={!videos[0]?.available || selectedFrames.left.length === 0 || captureStates.left}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    backgroundColor: captureStates.left ? '#6c757d' : (selectedFrames.left.length > 0 ? '#28a745' : '#6c757d'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (videos[0]?.available && selectedFrames.left.length > 0 && !captureStates.left) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    fontWeight: '500'
                  }}
                >
                  {captureStates.left ? 'Saving...' : `💾 Save ${selectedFrames.left.length} Frames`}
                </button>
            </div>
          </div>

            {/* Rear Camera */}
            <div style={{ 
              flex: '1 1 300px', 
              minWidth: '280px',
              maxWidth: 'calc(33.333% - 10px)',
              width: '100%'
            }}>
              <h5 style={{ marginBottom: '10px', color: '#555', textAlign: 'center' }}>Rear Camera</h5>
              {videos[1]?.available && videos[1]?.url && isValidVideoUrl(videos[1].url) ? (
                <div className={`video-container ${videoLoadingStates.center ? 'loading' : ''}`} style={{ 
                  position: 'relative', 
                  width: '100%',
                  height: '0',
                  paddingBottom: '56.25%', // 16:9 aspect ratio
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: 'transparent',
                  border: 'none',
                  boxShadow: 'none'
                }}>
                  <video
                    ref={centerVideoRef}
                    src={videos[1].url}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 0,
                      backgroundColor: 'transparent',
                      objectFit: 'cover',
                      filter: 'none',
                      opacity: 1,
                      brightness: 1,
                      contrast: 1,
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                    preload="metadata"
                    autoPlay={true}
                    muted={true}
                    loop={true}
                    playsInline={true}
                    title="Rear Camera Video"
                    onLoadStart={() => handleVideoLoadStart('center')}
                    onLoadedData={() => handleVideoLoaded('center')}
                    onError={(e) => handleVideoError('center', e)}
                    onPlay={handleVideoPlay}
                    onPause={handleVideoPause}
                    onTimeUpdate={() => handleVideoTimeUpdate('center')}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  color: '#666',
                  border: '2px solid #e9ecef',
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {videos[1]?.available ? 'Video format not supported or loading...' : 'No video available'}
                </div>
              )}
              
              {/* Frame Selection Controls for Rear Camera */}
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                <div style={{ marginBottom: '8px' }}>
                  <button
                    onClick={() => addFrameTimestamp('center')}
                    disabled={!videos[1]?.available}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: videos[1]?.available ? 'pointer' : 'not-allowed',
                      marginRight: '5px',
                      fontWeight: '500'
                    }}
                  >
                    ➕ Add Frame ({videoPlaybackTime.toFixed(1)}s)
                  </button>
                  <button
                    onClick={() => clearSelectedFrames('center')}
                    disabled={selectedFrames.center.length === 0}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedFrames.center.length > 0 ? 'pointer' : 'not-allowed',
                      fontWeight: '500'
                    }}
                  >
                    🗑️ Clear All
                  </button>
                </div>
                
                {/* Selected Frames Display */}
                {selectedFrames.center.length > 0 && (
                  <div style={{ 
                    marginBottom: '8px', 
                    padding: '6px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px',
                    fontSize: '10px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      Selected Frames ({selectedFrames.center.length}):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                      {selectedFrames.center.map((time, index) => (
                        <span
                          key={index}
                          style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '9px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px'
                          }}
                        >
                          {time.toFixed(1)}s
                          <button
                            onClick={() => removeFrameTimestamp('center', index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '8px',
                              padding: '0',
                              marginLeft: '2px'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Save Button */}
                <button
                  onClick={() => saveSelectedFrames('center')}
                  disabled={!videos[1]?.available || selectedFrames.center.length === 0 || captureStates.center}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    backgroundColor: captureStates.center ? '#6c757d' : (selectedFrames.center.length > 0 ? '#28a745' : '#6c757d'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (videos[1]?.available && selectedFrames.center.length > 0 && !captureStates.center) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    fontWeight: '500'
                  }}
                >
                  {captureStates.center ? 'Saving...' : `💾 Save ${selectedFrames.center.length} Frames`}
                </button>
              </div>
            </div>

            {/* Side Camera */}
            <div style={{ 
              flex: '1 1 300px', 
              minWidth: '280px',
              maxWidth: 'calc(33.333% - 10px)',
              width: '100%'
            }}>
              <h5 style={{ marginBottom: '10px', color: '#555', textAlign: 'center' }}>Side Camera</h5>
              {videos[2]?.available && videos[2]?.url && isValidVideoUrl(videos[2].url) ? (
                <div className={`video-container ${videoLoadingStates.right ? 'loading' : ''}`} style={{ 
                  position: 'relative', 
                  width: '100%',
                  height: '0',
                  paddingBottom: '56.25%', // 16:9 aspect ratio
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: 'transparent',
                  border: 'none',
                  boxShadow: 'none'
                }}>
                  <video
                    ref={rightVideoRef}
                    src={videos[2].url}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 0,
                      backgroundColor: 'transparent',
                      objectFit: 'cover',
                      filter: 'none',
                      opacity: 1,
                      brightness: 1,
                      contrast: 1,
                      boxShadow: 'none',
                      outline: 'none'
                    }}
                    preload="metadata"
                    autoPlay={true}
                    muted={true}
                    loop={true}
                    playsInline={true}
                    title="Side Camera Video"
                    onLoadStart={() => handleVideoLoadStart('right')}
                    onLoadedData={() => handleVideoLoaded('right')}
                    onError={(e) => handleVideoError('right', e)}
                    onPlay={handleVideoPlay}
                    onPause={handleVideoPause}
                    onTimeUpdate={() => handleVideoTimeUpdate('right')}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  color: '#666',
                  border: '2px solid #e9ecef',
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {videos[2]?.available ? 'Video format not supported or loading...' : 'No video available'}
                </div>
              )}
              
              {/* Frame Selection Controls for Side Camera */}
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                <div style={{ marginBottom: '8px' }}>
                  <button
                    onClick={() => addFrameTimestamp('right')}
                    disabled={!videos[2]?.available}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: videos[2]?.available ? 'pointer' : 'not-allowed',
                      marginRight: '5px',
                      fontWeight: '500'
                    }}
                  >
                    ➕ Add Frame ({videoPlaybackTime.toFixed(1)}s)
                  </button>
                  <button
                    onClick={() => clearSelectedFrames('right')}
                    disabled={selectedFrames.right.length === 0}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedFrames.right.length > 0 ? 'pointer' : 'not-allowed',
                      fontWeight: '500'
                    }}
                  >
                    🗑️ Clear All
                  </button>
                </div>
                
                {/* Selected Frames Display */}
                {selectedFrames.right.length > 0 && (
                  <div style={{ 
                    marginBottom: '8px', 
                    padding: '6px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px',
                    fontSize: '10px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      Selected Frames ({selectedFrames.right.length}):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                      {selectedFrames.right.map((time, index) => (
                        <span
                          key={index}
                          style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '9px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px'
                          }}
                        >
                          {time.toFixed(1)}s
                          <button
                            onClick={() => removeFrameTimestamp('right', index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '8px',
                              padding: '0',
                              marginLeft: '2px'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Save Button */}
                <button
                  onClick={() => saveSelectedFrames('right')}
                  disabled={!videos[2]?.available || selectedFrames.right.length === 0 || captureStates.right}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    backgroundColor: captureStates.right ? '#6c757d' : (selectedFrames.right.length > 0 ? '#28a745' : '#6c757d'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (videos[2]?.available && selectedFrames.right.length > 0 && !captureStates.right) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    fontWeight: '500'
                  }}
                >
                  {captureStates.right ? 'Saving...' : `💾 Save ${selectedFrames.right.length} Frames`}
                </button>
              </div>
              </div>
          </div>
        </div>

        {/* Master Video Controls */}
        <div className="master-controls" style={{ 
          marginTop: '20px',
          marginBottom: '15px',
          padding: '10px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div className="play-controls" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '15px', 
            marginBottom: '10px',
            flexWrap: 'wrap'
          }}>
              <button 
                onClick={togglePlayPause}
                style={{ 
                  padding: '12px 25px',
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '700',
                  backgroundColor: isPlaying ? '#dc3545' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.3)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  minWidth: '180px',
                  justifyContent: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.4)';
                  e.target.style.backgroundColor = isPlaying ? '#c82333' : '#218838';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                  e.target.style.backgroundColor = isPlaying ? '#dc3545' : '#28a745';
                }}
              >
                <span style={{ fontSize: '20px' }}>
                  {isPlaying ? '⏸️' : '▶️'}
                </span>
                <span>
                  {isPlaying ? 'Pause All Videos' : 'Play All Videos'}
                </span>
              </button>
            </div>
            
            <div className="time-navigation" style={{ marginBottom: '6px' }}>
              <input
                type="range"
                min="0"
                max={videoDuration?.duration_seconds || 0}
                value={currentTime}
                onChange={(e) => handleTimeChange(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  height: '4px',
                  borderRadius: '2px',
                  background: '#ddd',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                fontSize: '10px', 
                color: '#666',
                marginTop: '3px'
              }}>
                <span>{videoDuration?.start_time ? new Date(videoDuration.start_time).toLocaleTimeString() : '00:00:00'}</span>
                <span>{videoDuration?.end_time ? new Date(videoDuration.end_time).toLocaleTimeString() : '00:00:00'}</span>
              </div>
            </div>

            {/* Compact GPS Info */}
            {getCurrentGpsPoint() && (
              <div style={{
                padding: '6px 8px',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                fontSize: '10px',
                color: '#1976d2',
                lineHeight: '1.3'
              }}>
                <strong>📍</strong> {getCurrentGpsPoint().latitude.toFixed(4)}, {getCurrentGpsPoint().longitude.toFixed(4)} | 
                <strong> 🚗</strong> {getCurrentGpsPoint().speed.toFixed(1)} km/h | 
                <strong> 🧭</strong> {getCurrentGpsPoint().heading.toFixed(0)}°
              </div>
            )}
          </div>

      </div>
    </div>
  );
};

export default VideoDetails;
